import { useMutation } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { selectToken } from '@redux/slices/userSlice';
import { showAlert } from '@redux/slices/informationSlice';
import { coreService } from 'network-utils';
import { translate } from 'translations';
import { useState } from 'react';

// eslint-disable-next-line import/prefer-default-export
export const useCertificateShare = (): CertificateShareHookReturn => {
    const token = useSelector(selectToken);
    const [isCertUploading, setIsCertUploading] = useState<boolean>(false);

    const dispatch = useDispatch();
    const errorString = translate('Something went wrong, please try again later');

    const showError = (error: string) => {
        dispatch(
            showAlert({
                value: error,
                status: 'error'
            })
        );
    };

    const submitUploadCertificateToLinkedin = async (input: {
        imageDataUri: string;
    }): Promise<coreService.LinkedinData> => {
        const result = await coreService.learnApi.postCertificateToLinkedin(token, input);
        return result;
    };

    const onUploadCertificateToLinkedin = (imageDataUri: string) => {
        mutate({
            imageDataUri
        });
    };

    const openLinkedinPopup = (url: string) => {
        const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        window.open(shareUrl, '_blank', 'noreferrer');
    };

    const { mutate } = useMutation(submitUploadCertificateToLinkedin, {
        onSuccess: (postLinkedinResponse: coreService.LinkedinData) => {
            if (postLinkedinResponse?.json?.success) {
                openLinkedinPopup(postLinkedinResponse?.json?.certificateUri);
            } else {
                showError(errorString);
            }
        },
        onError: () => {
            showError(errorString);
        },
        onSettled: () => {
            localStorage.removeItem('courseCertificateUuid');
            setIsCertUploading(false);
        }
    });

    return {
        onUploadCertificateToLinkedin,
        isCertUploading,
        setIsCertUploading,
        showError
    };
};

type CertificateShareHookReturn = {
    onUploadCertificateToLinkedin: (imageDataUri: string) => void;
    isCertUploading: boolean;
    setIsCertUploading: React.Dispatch<React.SetStateAction<boolean>>;
    showError: (error: string) => void;
};
